import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Divider, Grid } from '@mui/material';
import { MarketLink } from '@market/react';
import { toCurrency, toCurrencyWithMinorUnitAmount } from 'utilities/currency';
import ActionablePromotion from 'components/common/receipt/actionable_promotion';
import ReceiptItem from 'components/common/receipt/receipt_item';
import IssuerInstallmentsPrice from 'components/common/receipt/issuer_installments_price';
import TaxReceiptItem from 'components/common/receipt/tax_receipt_item';
import FinancingModal from 'components/pages/product/financing_modal';
import PageLoader from 'components/common/page_loader';
import PromoAdd from 'components/common/promo_add';
import {
  getSoftwarePriceLabel,
  sumSoftwarePricing,
  sortProductByHardwareOrSoftware,
} from 'utilities/software';
import { CurrencyCode } from 'rpc/model/squareup/common/currency_codes';

const CartReceipt = ({
  loading = false,
  orderData,
  financeData,
  cartClient,
  orderRefresh,
}) => {
  const { t } = useTranslation();
  const [disabled, setDisabled] = useState(false);
  const [viewFinance, setViewFinance] = useState(false);

  const { software: softwareProducts } = sortProductByHardwareOrSoftware(
    orderData.cart_summary,
  );

  const { amount: softwareCostTotal, currencyCode: softwareCurrency } =
    sumSoftwarePricing(
      softwareProducts
        .map((product) => product.plan)
        .filter((product) => product !== null),
    );

  const removePromotion = (promoCode) => {
    setDisabled(true);
    cartClient
      .removePromotion(promoCode)
      .then(orderRefresh)
      .finally(() => setDisabled(false));
  };

  const promotions = orderData.discounts.map((promo, index) => (
    <ActionablePromotion
      key={`discount-${index}`}
      disabled={disabled}
      code={promo.code}
      adjustments={promo.adjustments}
      currency={promo.currency}
      canRemove={!promo.auto_applied}
      didReachCartLimit={promo.limit_reached}
      onRemove={removePromotion}
    />
  ));

  if (loading) {
    return <PageLoader />;
  }

  return (
    <Grid
      container
      direction="column"
      sx={{
        border: 1,
        borderColor: 'grey.300',
        borderRadius: 1.5,
        boxSizing: 'border-box',
        py: 4,
      }}
    >
      {/* Hardware Section */}
      <Box
        sx={{
          px: 3,
        }}
      >
        <Box
          sx={{
            mb: 2,
          }}
        >
          <b className="semibold-20">{t('common.hardware').toUpperCase()}</b>
        </Box>
        <ReceiptItem
          label={'checkout.receipt.label.subtotal'}
          cost={orderData.subtotal.amount}
          currency={orderData.subtotal.currency}
        />
        <ReceiptItem
          label={orderData.shipping.label}
          cost={orderData.shipping.amount}
          currency={orderData.shipping.currency}
          isShipping={true}
        />
        <TaxReceiptItem orderData={orderData} />
        {promotions.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            {promotions}
          </>
        )}
        <Divider sx={{ my: 2 }} />
        <ReceiptItem
          label={orderData.total.label}
          cost={orderData.total.amount}
          currency={orderData.total.currency}
          topOffset={1}
          bottomOffset={1}
          emphasize
        />
        {orderData.total.amount > 0 && financeData && (
          <MarketLink
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setViewFinance(true);
            }}
            className="medium-20"
            style={{ textAlign: 'right' }}
          >
            {t('productPage.financingModalTrigger', {
              price: toCurrency(financeData.amount, financeData.currency),
              period: financeData.number_payment_periods,
            })}
          </MarketLink>
        )}
        {orderData.issuer_installments_summary?.quote_payments_amount && (
          <IssuerInstallmentsPrice
            issuerInstallmentsDetails={orderData.issuer_installments_summary}
          />
        )}
        <Divider sx={{ my: 2 }} />
        <PromoAdd cartClient={cartClient} orderRefresh={orderRefresh} />
        <FinancingModal
          isLoggedIn={orderData.current_user.is_logged_in}
          open={viewFinance}
          handleClose={() => setViewFinance(false)}
        />
      </Box>
      {/* Software Section */}
      {softwareProducts.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box
            sx={{
              px: 3,
            }}
          >
            <Box
              sx={{
                my: 2,
              }}
            >
              <b className="semibold-20">
                {t('common.software').toUpperCase()}
              </b>
            </Box>
            {softwareProducts.map((product) => (
              <ReceiptItem
                key={product.id}
                label={product.product_variant.title}
                override={getSoftwarePriceLabel({ plan: product?.plan, t })}
              />
            ))}
            <Divider sx={{ my: 2 }} />
            <ReceiptItem
              label={t('checkout.receipt.label.estimatedMonthlyBill')}
              override={toCurrencyWithMinorUnitAmount(
                softwareCostTotal,
                CurrencyCode[softwareCurrency],
              )}
              emphasize
            />
            <Box mt={2}>
              <p className="paragraph-20">
                {t('checkout.receipt.label.softwareBillingDisclaimer')}
              </p>
            </Box>
          </Box>
        </>
      )}
    </Grid>
  );
};

export default CartReceipt;
